<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMutation, useQuery } from '@/composables/api'

import { useDialogsStore } from '@/stores/dialogsStore'

import { normalizeBankAccount, normalizeGiro } from '@/utils/bankAccount'
import { bankgiroIssue, isValidBankAccount, isValidNormalizedPlusgiroNumber } from '@/utils/validation'

import type { CustomerUser } from '@/types/customer-users'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import FPayoutMethodFields from '@/components/feature/FPayoutMethodFields/FPayoutMethodFields.vue'
import ContactFields from './ContactFields.vue'
import type { PayoutMethod } from '@/components/feature/FPayoutMethodFields/types'

// LEGACY BRIDGE (dies with the navigation/Profile port): after a successful PATCH the merged user
// is ALSO written back to the Vuex `user` slice — the routed legacy Profile view and the
// Datadog/Survicate identity (useLegacyBootstrap) still read it and must not see a stale one.
import { store as legacyStoreUntyped } from '@old/store'

// Singleton profile dialog (ADA-4437 etapp 3) — replaces @old profile_dialog. Two pages:
// contact (mobile + email) and payout details; the request_invoice_copy context is a
// persistent mobile-only flow. Opens via dialogsStore.openProfile() — which ALWAYS opens:
// only the Home nudge gates on dontShowProfileDialog (deliberate fix of the legacy hang
// where the flag suppressed the invoice-copy flow too).
const legacyStore = legacyStoreUntyped as {
  state: { user?: (Record<string, unknown> & { _id?: string }) | null }
  dispatch(action: 'set_state_property', payload: { property: string; data: unknown }): Promise<unknown>
}

const { t } = useI18n()
const dialogsStore = useDialogsStore()

const model = computed({
  get: () => dialogsStore.profile != null,
  set: (value) => {
    // ESC / click-outside / header X — the skip path (the save path closes programmatically).
    if (!value) skipClose()
  },
})
const isMobileNumberOnly = computed(() => dialogsStore.profile?.context === 'request_invoice_copy')

const userQuery = useQuery('/customer--users')
const user = computed<CustomerUser | undefined>(() => userQuery.data.value?._items?.[0])

// The dialog's pages, in order. The header counter derives from this list — no literal page
// count in the template (PR #896 review).
const PAGES = ['contact', 'payout'] as const
type ProfilePage = (typeof PAGES)[number]
const page = ref<ProfilePage>('contact')
const pageNumber = computed(() => PAGES.indexOf(page.value) + 1)
const formValid = ref(false)
const email = ref('')
const mobileNumber = ref('')
// ADA-4345 step 2.15: the debtor picks how they want to be paid, not only a bank account.
// `payout_details` can physically hold every instrument at once, but the profile expresses a
// single choice — see buildUpdates.
const payoutMethod = ref<PayoutMethod>('bank_account')
const clearingNumber = ref('')
const accountNumber = ref('')
const bankgiro = ref('')
const plusgiro = ref('')

function storedPayoutMethod(): PayoutMethod {
  if (accountNumber.value !== '') return 'bank_account'
  if (bankgiro.value !== '') return 'bankgiro'
  if (plusgiro.value !== '') return 'plusgiro'
  return 'bank_account'
}

// Seed the fields when the dialog opens (and when the user data arrives while open).
// Legacy parity: the full flow jumps straight to the payout page when contact data already exists.
watch(
  () => [dialogsStore.profile != null, user.value?._id] as const,
  ([open]) => {
    if (!open || user.value == null) return
    email.value = user.value.email_data?.email ?? ''
    mobileNumber.value = user.value.mobile_number_data?.mobile_number ?? ''
    clearingNumber.value = user.value.payout_details?.bank_account_number?.clearing_number ?? ''
    accountNumber.value = user.value.payout_details?.bank_account_number?.account_number ?? ''
    bankgiro.value = user.value.payout_details?.bankgiro ?? ''
    plusgiro.value = user.value.payout_details?.plusgiro ?? ''
    // Same priority as the refund wizard's prefill (Overpayments.vue), so the profile opens on
    // the method the wizard would preselect. FPayoutMethodFields corrects it for a company.
    payoutMethod.value = storedPayoutMethod()
    page.value = !isMobileNumberOnly.value && email.value !== '' && mobileNumber.value !== '' ? 'payout' : 'contact'
  },
  { immediate: true }
)

// The giro numbers the record already carries — the reference for every change-aware check below.
const storedBankgiro = computed(() => user.value?.payout_details?.bankgiro ?? '')
const storedPlusgiro = computed(() => user.value?.payout_details?.plusgiro ?? '')

/**
 * Whether a field value is the stored bankgiro, judged normalized: one number, one answer.
 *
 * A record with no bankgiro is never "the stored one": normalizing takes both an absent giro and
 * an all-zero input to '', so a fresh '0000000' would otherwise pass as untouched and reach the
 * api, which answers it with a 422. `isStoredPlusgiro` below is the same rule for the other
 * instrument.
 */
function isStoredBankgiro(value: string): boolean {
  const stored = normalizeGiro(storedBankgiro.value)
  return stored !== '' && normalizeGiro(value) === stored
}

function isStoredPlusgiro(value: string): boolean {
  const stored = normalizeGiro(storedPlusgiro.value)
  return stored !== '' && normalizeGiro(value) === stored
}

const primaryDisabled = computed(() => {
  // saveAndClose can only PATCH once the user document (with _id/_etag) has resolved —
  // submitting before that would silently drop the input and close the dialog.
  if (user.value == null) return true
  if (page.value === 'contact') {
    if (isMobileNumberOnly.value) return !formValid.value
    return email.value === '' || mobileNumber.value === '' || !formValid.value
  }
  // The registry gate (ADA-4344): form-shape validity no longer suffices for a bank account —
  // what the registry proves impossible would 422 on the PATCH (x_rule), so the save is held
  // until the pair is payable. Empty still passes: clearing the fields stays a valid no-op.
  if (
    payoutMethod.value === 'bank_account' &&
    (clearingNumber.value !== '' || accountNumber.value !== '') &&
    !isValidBankAccount(clearingNumber.value, accountNumber.value)
  ) {
    return true
  }
  // The specification gate (ADA-4350): the bankgiro x_rule answers an impossible number with a
  // 422 on the PATCH, so the save is held until it is payable. Empty still passes — clearing the
  // field stays a valid no-op, exactly like the bank account above.
  //
  // Change-aware, like the x_rule itself: a number stored before the rule existed is skipped
  // server-side when the PATCH does not change it, so holding the save on it here would lock the
  // debtor out of editing their mobile number over a value they never entered on this screen.
  if (
    payoutMethod.value === 'bankgiro' &&
    bankgiro.value !== '' &&
    !isStoredBankgiro(bankgiro.value) &&
    bankgiroIssue(bankgiro.value) !== undefined
  ) {
    return true
  }
  // The form gate for plusgiro (ADA-4349): only its length blocks, and only after normalization —
  // '00' and '007' pass the field regex but strip to below the two-digit floor, which the api
  // rejects as `invalid_plusgiro_length`. The check digit merely warns (decision #1), so it must
  // NOT be part of this gate. Empty passes, like the two instruments above, and so does a stored
  // value left untouched — same change-aware reason as the bankgiro branch: a plusgiro that
  // normalizes below the floor was written before this rule existed, and re-judging it here would
  // hold the debtor's unrelated edits hostage to a number they cannot fix from this screen.
  if (
    payoutMethod.value === 'plusgiro' &&
    plusgiro.value !== '' &&
    !isStoredPlusgiro(plusgiro.value) &&
    !isValidNormalizedPlusgiroNumber(plusgiro.value)
  ) {
    return true
  }
  return !formValid.value
})

const patchUser = useMutation('patch', '/customer--users/{resource_id}', {
  // The dialog closing IS the confirmation (legacy parity) — errors still get the global snackbar.
  meta: { notifyOnSuccess: false },
})

function handlePrimary() {
  if (page.value === 'contact' && !isMobileNumberOnly.value) {
    page.value = 'payout'
  } else {
    void saveAndClose()
  }
}

type PayoutDetails = NonNullable<CustomerUser['payout_details']>

/** Whether a stored giro and a candidate are the same number, ignoring spelling. */
function sameGiro(stored: string | null | undefined, next: string | null | undefined): boolean {
  return normalizeGiro(stored ?? '') === normalizeGiro(next ?? '')
}

/**
 * The payout details to PATCH, or null when nothing changed.
 *
 * The selected method wins and the other two instruments are nulled (developer decision,
 * 19 Aug 2026): `payout_details` can hold a bank account, a bankgiro and a plusgiro at the same
 * time, and the refund wizard resolves that ambiguity by a fixed priority — bank account first.
 * Leaving an old account behind would mean the debtor picks bankgiro here and the wizard still
 * preselects the account, which reads as the choice not having been taken.
 *
 * `iban_bic` is preserved: no surface in Mitt Amili collects or shows it, so nulling it would
 * discard a value the debtor was never given a chance to re-enter.
 */
function buildPayoutDetails(current: CustomerUser): PayoutDetails | null {
  // Stored normalized (ADA-4344 decision #5), so the confirmed value is the paid value.
  const normalized = normalizeBankAccount(clearingNumber.value, accountNumber.value)
  const account =
    payoutMethod.value === 'bank_account' && (clearingNumber.value !== '' || accountNumber.value !== '')
      ? { clearing_number: normalized.clearingNumber, account_number: normalized.accountNumber }
      : null

  const next: PayoutDetails = {
    ...(current.payout_details ?? {}),
    bank_account_number: account,
    // Both giro numbers stored normalized (ADA-4350 decision #5, plusgiro added in ADA-4349), so
    // the same number cannot end up stored in two spellings.
    bankgiro: payoutMethod.value === 'bankgiro' && bankgiro.value !== '' ? normalizeGiro(bankgiro.value) : null,
    plusgiro: payoutMethod.value === 'plusgiro' && plusgiro.value !== '' ? normalizeGiro(plusgiro.value) : null,
  }

  const currentAccount = current.payout_details?.bank_account_number ?? null
  // The giro comparison runs NORMALIZED on both sides (ADA-4350 review). The stored value can
  // predate the normalization, so '01609908' against the normalized candidate '1609908' would
  // read as a change that is not one — and any change here PATCHes the whole payout_details
  // object, nulling the other instruments as a side effect of editing a mobile number.
  const unchanged =
    (currentAccount?.clearing_number ?? '') === (account?.clearing_number ?? '') &&
    (currentAccount?.account_number ?? '') === (account?.account_number ?? '') &&
    sameGiro(current.payout_details?.bankgiro, next.bankgiro) &&
    sameGiro(current.payout_details?.plusgiro, next.plusgiro)

  return unchanged ? null : next
}

// Diff-based PATCH (legacy parity): only changed fields are sent, contact changes reset
// `verified`, payout_details is patched as the whole merged object.
function buildUpdates(current: CustomerUser): Record<string, unknown> {
  const updates: Record<string, unknown> = {}
  const strippedMobile = mobileNumber.value.replaceAll(' ', '')
  if (strippedMobile !== (current.mobile_number_data?.mobile_number ?? '')) {
    updates.mobile_number_data = { mobile_number: strippedMobile, verified: false }
  }
  if (email.value !== (current.email_data?.email ?? '')) {
    updates.email_data = { email: email.value, verified: false }
  }
  const payoutDetails = buildPayoutDetails(current)
  if (payoutDetails != null) {
    updates.payout_details = payoutDetails
  }
  // The address is deliberately NOT collected here (developer decision 19 Aug): this dialog is
  // what greets the debtor at login, and asking for a postal address in that prompt is more than
  // the moment warrants. It stays editable on the profile page, which is where
  // `overpayments.issue_address_missing` already sends anyone blocked by a missing one.

  // The mobile-only flow never shows the email or payout fields — drop their no-op diffs.
  if (isMobileNumberOnly.value) {
    delete updates.email_data
    delete updates.payout_details
  }
  return updates
}

async function saveAndClose() {
  const current = user.value
  if (current != null) {
    const updates = buildUpdates(current)
    if (Object.keys(updates).length > 0) {
      try {
        // Eve's PATCH response carries the fresh meta (_etag/_updated) — the generated type
        // says void (swagger lies about the response here).
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        const response = (await patchUser.mutateAsync({
          path: { resource_id: current._id },
          header: { 'If-Match': current._etag },
          // @ts-expect-error Swagger lies (body only)
          body: updates,
        })) as unknown as Record<string, unknown>
        // LEGACY BRIDGE (dies with the navigation/Profile port): keep the Vuex user in sync.
        void legacyStore.dispatch('set_state_property', {
          property: 'user',
          data: { ...(legacyStore.state.user ?? {}), ...response, ...updates },
        })
      } catch {
        return // error snackbar comes from the global handler; keep the dialog open
      }
    }
  }
  finishClose()
}

function skipClose() {
  finishClose()
}

// Every close path suppresses the Home nudge (legacy parity) — the flag no longer
// affects openProfile() flows, only the nudge gate.
function finishClose() {
  dialogsStore.dontShowProfileDialog = true
  sessionStorage.setItem('dont_show_profile_dialog', 'true')
  dialogsStore.closeProfile()
}

function afterLeave() {
  page.value = 'contact'
}
</script>

<template>
  <b-dialog
    v-model="model"
    :persistent="isMobileNumberOnly"
    hide-footer
    @after-leave="afterLeave"
  >
    <template #title>
      {{ t(page === 'contact' ? 'common.how_may_we_be_in_touch' : 'common.payout_details') }}
    </template>

    <template #header-actions>
      <span
        v-if="!isMobileNumberOnly"
        class="text-body-small text-no-wrap mr-2"
      >
        {{ pageNumber }} / {{ PAGES.length }}
      </span>
    </template>

    <v-form
      v-model="formValid"
      @submit.prevent="handlePrimary"
    >
      <contact-fields
        v-if="page === 'contact'"
        v-model:email="email"
        v-model:mobile-number="mobileNumber"
        :mobile-number-only="isMobileNumberOnly"
      />

      <f-payout-method-fields
        v-if="page === 'payout'"
        v-model:method="payoutMethod"
        v-model:clearing-number="clearingNumber"
        v-model:account-number="accountNumber"
        v-model:bankgiro="bankgiro"
        v-model:plusgiro="plusgiro"
        :stored-bankgiro="storedBankgiro"
        :stored-plusgiro="storedPlusgiro"
      />

      <div class="d-flex pt-2 px-6 pb-4">
        <v-spacer />

        <v-btn
          variant="text"
          class="mr-2"
          @click="skipClose"
        >
          {{ t(isMobileNumberOnly ? 'common.cancel' : 'common.skip') }}
        </v-btn>
        <v-btn
          :disabled="primaryDisabled"
          :loading="patchUser.isPending.value"
          color="primary"
          variant="flat"
          type="submit"
        >
          {{ t(page === 'contact' && !isMobileNumberOnly ? 'common.next' : 'common.send_in') }}
        </v-btn>
      </div>
    </v-form>
  </b-dialog>
</template>
